import { useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, Select, useToast } from '../../../design-system';
import '../../styles/Attendance.css';

// Fix a day: someone forgot to clock out, or was on approved leave.
//
// The reason is mandatory and the server refuses without one — an attendance
// register anyone can rewrite silently is not a register. Every save stamps who
// changed it and why, and that shows on the employee's own report too.

const STATUSES = [
  { value: 'present', label: 'Present' },
  { value: 'half_day', label: 'Half day' },
  { value: 'leave', label: 'Leave' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'week_off', label: 'Weekly off' },
];

// <input type="datetime-local"> needs local wall-clock text, and gives it back
// the same way — so both directions go through the browser's own offset rather
// than slicing an ISO string, which would silently shift by the UTC offset.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

function AttendanceCorrectionModal({ userId, name, dateKey, onClose, onSaved }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('present');
  const [sessions, setSessions] = useState([]);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/api/attendance/user/${userId}`, { params: { month: dateKey.slice(0, 7) } })
      .then((r) => {
        if (cancelled) return;
        const day = (r.data.days || []).find((d) => d.dateKey === dateKey);
        if (day) {
          setStatus(['absent', 'upcoming'].includes(day.status) ? 'present' : day.status);
          setSessions((day.sessions || []).map((s) => ({
            inAt: toLocalInput(s.inAt), outAt: toLocalInput(s.outAt),
          })));
          setNote(day.note || '');
        }
      })
      .catch((e) => !cancelled && toast.danger(e.response?.data?.error || 'Could not load that day'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dateKey]);

  const setSession = (i, field, value) =>
    setSessions((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));

  const save = async () => {
    if (!reason.trim()) { toast.danger('A reason is required'); return; }
    setSaving(true);
    try {
      await apiClient.put('/api/attendance/record', {
        userId,
        dateKey,
        status,
        note,
        reason: reason.trim(),
        sessions: sessions
          .filter((s) => s.inAt)
          .map((s) => ({ inAt: fromLocalInput(s.inAt), outAt: fromLocalInput(s.outAt) })),
      });
      toast.success('Attendance corrected');
      onSaved?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save the correction');
    } finally {
      setSaving(false);
    }
  };

  const owesHours = status === 'present' || status === 'half_day';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Correct attendance — ${name}, ${dateKey}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={loading || saving}>Save</Button>
        </>
      }
    >
      {loading ? <p className="att-empty">Loading…</p> : (
        <div className="att-form">
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>

          {!owesHours && (
            <p className="att-hint att-hint-inline">
              A {STATUSES.find((s) => s.value === status)?.label.toLowerCase()} day owes no standard
              hours, so it won't show a shortfall. Anything worked on it counts as overtime.
            </p>
          )}

          <div className="att-sess-list">
            <span className="att-form-label">Clock in / out</span>
            {sessions.map((s, i) => (
              <div className="att-sess-row" key={i}>
                <input
                  type="datetime-local" className="ds-input"
                  value={s.inAt} onChange={(e) => setSession(i, 'inAt', e.target.value)}
                />
                <span className="att-sess-arrow">→</span>
                <input
                  type="datetime-local" className="ds-input"
                  value={s.outAt} onChange={(e) => setSession(i, 'outAt', e.target.value)}
                />
                <button
                  type="button" className="att-sess-del"
                  onClick={() => setSessions((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label="Remove this session"
                >✕</button>
              </div>
            ))}
            <Button
              variant="secondary" size="sm"
              onClick={() => setSessions((prev) => [...prev, { inAt: `${dateKey}T10:00`, outAt: '' }])}
            >
              + Add a session
            </Button>
            <p className="att-hint att-hint-inline">
              Two sessions with a gap between them is how a break is recorded. Only the time inside a
              session counts as worked.
            </p>
          </div>

          <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <Input
            label="Reason for this correction (required)"
            placeholder="e.g. forgot to clock out, approved leave"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}

export default AttendanceCorrectionModal;
